import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import PhishingDashboardPreview from '@/images/placeholders/phishing-dashboard-preview.png';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

export type PhishingAnalyticsEmptyStateVariant =
  | 'free-trial'
  | 'auto-phishing'
  | 'awt-customer'
  | 'simulation-running';

interface PhishingAnalyticsEmptyStateProps {
  variant: PhishingAnalyticsEmptyStateVariant;
}

const StyledContainer = styled(FlexContainer)`
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: 70px var(--big-padding) 80px;
  width: 100%;
`;

const StyledImageArea = styled.div`
  width: 628px;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: auto;
  }
`;

const StyledTitle = styled.h2`
  font-size: 25px;
  line-height: 1.5;
  font-weight: 700;
  margin: 0;
  text-align: center;
  width: 620px;
  max-width: 100%;
`;

const StyledDescription = styled.p`
  font-size: var(--medium-font-size);
  line-height: 1.5;
  font-weight: 400;
  text-align: center;
  margin: 0;
  width: 558px;
  max-width: 100%;
`;

const StyledBenefitsCard = styled.div`
  display: flex;
  background-color: var(--white-main);
  border: 1px solid #e4e4e4;
  border-radius: var(--xsmall-border-radius);
  width: 628px;
  max-width: 100%;
  height: 92px;
`;

const StyledBenefit = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--medium-padding);
  font-size: var(--xsmall-font-size);
  line-height: 1.5;
  color: var(--black-main);
  gap: 7px;

  &:not(:last-child) {
    border-right: 1px solid #e4e4e4;
  }

  strong {
    font-weight: 600;
  }
`;

const CheckmarkIcon = () => (
  <svg
    width="17"
    height="13"
    viewBox="0 0 17 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M5.94479 13L0 6.83784L1.4862 5.2973L5.94479 9.91892L15.5138 0L17 1.54054L5.94479 13Z"
      fill="var(--red-main)"
    />
  </svg>
);

const VARIANT_CONFIG: Record<
  PhishingAnalyticsEmptyStateVariant,
  {
    titleKey: string;
    descriptionKey: string;
    ctaKey?: string;
    ctaRoute?: string;
    showBenefits: boolean;
    imageFirst: boolean;
  }
> = {
  'free-trial': {
    titleKey: 'phishing.analytics.empty.freeTrial.title',
    descriptionKey: 'phishing.analytics.empty.freeTrial.description',
    ctaKey: 'phishing.analytics.empty.freeTrial.seeCatalogue',
    ctaRoute: RedirectPaths[RedirectPathsEnum.CAMPAIGNS_CATALOGUE_V2](),
    showBenefits: true,
    imageFirst: true,
  },
  'auto-phishing': {
    titleKey: 'phishing.analytics.empty.autoPhishing.title',
    descriptionKey: 'phishing.analytics.empty.autoPhishing.description',
    ctaKey: 'phishing.analytics.empty.autoPhishing.cta',
    ctaRoute: RedirectPaths[RedirectPathsEnum.PHISHING_SIMULATIONS](),
    showBenefits: true,
    imageFirst: true,
  },
  'awt-customer': {
    titleKey: 'phishing.analytics.empty.customer.title',
    descriptionKey: 'phishing.analytics.empty.customer.description',
    ctaKey: 'phishing.analytics.empty.customer.cta',
    ctaRoute: RedirectPaths[RedirectPathsEnum.CAMPAIGNS_CATALOGUE_V2](),
    showBenefits: true,
    imageFirst: true,
  },
  'simulation-running': {
    titleKey: 'phishing.analytics.empty.simulationRunning.title',
    descriptionKey: 'phishing.analytics.empty.simulationRunning.description',
    showBenefits: false,
    imageFirst: false,
  },
};

const BENEFIT_KEYS = [
  'phishing.analytics.empty.benefit.progress',
  'phishing.analytics.empty.benefit.highRisk',
  'phishing.analytics.empty.benefit.benchmark',
];

export const PhishingAnalyticsEmptyState: React.FC<
  PhishingAnalyticsEmptyStateProps
> = ({ variant }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = VARIANT_CONFIG[variant];

  const imageSection = (
    <StyledImageArea>
      <img src={PhishingDashboardPreview} alt={t(config.titleKey)} />
    </StyledImageArea>
  );

  const textSection = (
    <FlexContainer direction="column" align="center" gap={16}>
      <StyledTitle>{t(config.titleKey)}</StyledTitle>
      <StyledDescription>{t(config.descriptionKey)}</StyledDescription>
    </FlexContainer>
  );

  const buttonSection = config.ctaKey && config.ctaRoute && (
    <AppButton
      label={t(config.ctaKey)}
      severity="danger"
      onClick={() => navigate(config.ctaRoute as string)}
    />
  );

  const contentSection = config.imageFirst ? (
    <>
      <FlexContainer direction="column" align="center" gap={40}>
        {imageSection}
        {textSection}
      </FlexContainer>
      {buttonSection}
    </>
  ) : (
    <FlexContainer direction="column" align="center" gap={40}>
      {textSection}
      {imageSection}
    </FlexContainer>
  );

  return (
    <StyledContainer direction="column" align="center" gap={50} flex="1">
      <FlexContainer direction="column" align="center" gap={32}>
        {contentSection}
      </FlexContainer>
      {config.showBenefits && (
        <StyledBenefitsCard>
          {BENEFIT_KEYS.map((key) => (
            <StyledBenefit key={key}>
              <CheckmarkIcon />
              <span dangerouslySetInnerHTML={{ __html: t(key) }} />
            </StyledBenefit>
          ))}
        </StyledBenefitsCard>
      )}
    </StyledContainer>
  );
};
